import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/common";
import { AurumOrb } from "@/components/brand/AurumOrb";
import { Send } from "lucide-react";

const QUICK = [
  "What is Gold doing right now?", "Why are we waiting?",
  "Explain today's bias.", "What news matters today?",
  "What would invalidate the long thesis?", "Calculate my position size.",
];

export default function AskAurum() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/ai/history").then(({ data }) => setMessages(data.messages || [])).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (q) => {
    const question = q || input;
    if (!question.trim() || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { question });
      setMessages((m) => [...m, { role: "assistant", text: data.text }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const is402 = e?.response?.status === 402;
      setMessages((m) => [...m, { role: "assistant",
        text: is402 && typeof detail === "string"
          ? `${detail}\n\nUpgrade your plan for more AI requests → Settings → Subscription.`
          : "AI TEMPORARILY UNAVAILABLE — please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-9rem)] max-w-3xl mx-auto animate-fade-up">
      <div className="flex items-center gap-3 mb-4">
        <AurumOrb status={loading ? "ANALYZING" : "IDLE"} size={44} />
        <div><h1 className="text-xl font-bold tracking-tight">Ask Aurum</h1><p className="text-xs text-[var(--text-muted)]">Grounded in your live terminal data. Cites real numbers.</p></div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="ask-messages">
        {messages.length === 0 && (
          <Card className="p-5">
            <p className="text-sm text-[var(--text-sec)]">Ask about the current market, your risk, or your trades. Try a quick question:</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {QUICK.map((q) => (
                <button key={q} onClick={() => send(q)} className="text-xs rounded-full border border-[var(--border2)] px-3 py-1.5 hover:border-aurum hover:text-aurum transition-colors" data-testid="quick-question">{q}</button>
              ))}
            </div>
          </Card>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
              m.role === "user" ? "btn-gold font-medium" : "bg-[var(--card2)] border border-[var(--border2)] text-[var(--text-sec)]"
            }`} style={m.role === "assistant" ? { borderLeft: "2px solid #D6AE57" } : {}}>
              {m.text}
            </div>
          </div>
        ))}
        {loading && <div className="text-xs text-[var(--text-muted)] flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-aurum animate-pulse" /> Aurum is analyzing…</div>}
        <div ref={endRef} />
      </div>

      <div className="mt-4 flex items-center gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask Aurum about Gold…" data-testid="ask-input"
          className="flex-1 rounded-full bg-[var(--surface)] border border-[var(--border2)] px-4 py-3 text-sm outline-none focus:border-aurum" />
        <button onClick={() => send()} disabled={loading} data-testid="ask-send" className="btn-gold rounded-full p-3 hover:brightness-110 disabled:opacity-50"><Send size={18} /></button>
      </div>
    </div>
  );
}
